import React, { memo, useMemo } from 'react';
import { validators } from 'investira.sdk';
import { Stack, Typography, CircularProgress, Divider, Box } from 'investira.react.components';
import {
  AtivoConsistencia,
  AtivoIndicesRentabilidade,
  AtivoPatrimonio,
  AtivoRentabilidade,
  AtivoMetricasRisco,
  SearchGlobal,
  AtivoInformacoes
} from '../../../../components/organisms';
import { AtivoTemplate } from '../../../../components/templates';
import { useAtivosContext } from '../../../../contexts/AtivosContext';
import AtivoHeader from '../AtivoHeader';
import AtivoPessoa from '../AtivoPessoa';
import AtivoHighlight from '../AtivoHighlight';
import AtivoCotacao from '../AtivoCotacao';
import AtivoIndicadores from '../AtivoIndicadores';
import AtivoProventos from '../AtivoProventos';
import AtivosSemelhantes from '../AtivosSemelhantes';
import AtivoCarteira from '../AtivoCarteira';
import AtivoImoveis from '../AtivoImoveis';
import AtivoGestorAdministrador from '../AtivoGestorAdministrador';
import Graphs from '../Graphs';
import { getAtivoDisplayMode } from '../../../../components/organisms/ativoDisplayMode';
import {
  ATIVO_OVERVIEW_SECTION,
  getAtivoOverviewSectionOrder,
  getAtivoOverviewSections
} from './ativoOverviewSections';
import { getAtivoDefaultBenchmark } from './ativoOverviewBenchmark';

const AtivoOverview = memo(() => {
  const { state } = useAtivosContext();
  const { pessoa, ativo, cotacao, ativo_avaliacao, isPageLoading, isAvaliacaoLoading } = state;
  const xPessoa = pessoa || { ...ativo?.emissor, pessoa_id: ativo?.emissor_pessoa_id || null };
  const xAtivoLabel = ativo.ticker || ativo.ativo_seuid || ativo.ativo;
  const xDisplayMode = useMemo(() => getAtivoDisplayMode(ativo), [ativo]);
  const xOverviewSections = useMemo(
    () => getAtivoOverviewSections(xDisplayMode, ativo),
    [ativo, xDisplayMode]
  );
  const xOverviewSectionOrder = useMemo(
    () => getAtivoOverviewSectionOrder(xDisplayMode),
    [xDisplayMode]
  );
  const xDefaultBenchmark = useMemo(
    () =>
      getAtivoDefaultBenchmark({
        ativo,
        ativoAvaliacao: ativo_avaliacao,
        displayMode: xDisplayMode
      }),
    [ativo, ativo_avaliacao, xDisplayMode]
  );

  const xAtivoCotacaoData = useMemo(() => {
    return [
      {
        ativo: xAtivoLabel,
        data: cotacao.historico.full
      }
    ];
  }, [xAtivoLabel, cotacao.historico.full]);

  const xCotacoesPorId = useMemo(() => {
    if (!ativo.ifeed_ativo_id) return {};

    return {
      [ativo.ifeed_ativo_id]: {
        data: cotacao.data || cotacao.historico.full || [],
        loading: Boolean(cotacao.loading),
        error: Boolean(cotacao.error)
      }
    };
  }, [ativo.ifeed_ativo_id, cotacao.data, cotacao.error, cotacao.historico.full, cotacao.loading]);

  const xCharts = useMemo(() => {
    return {
      ativos: ativo_avaliacao?.avaliacao?.ativos || [],
      emissores: ativo_avaliacao?.avaliacao?.emissores || [],
      gestores: ativo_avaliacao?.avaliacao?.gestores || [],
      setores: ativo_avaliacao?.avaliacao?.setores || []
    };
  }, [ativo_avaliacao]);
  const xContentBySection = {
    [ATIVO_OVERVIEW_SECTION.rentabilidade]: (
      <AtivoRentabilidade
        ativos={[ativo]}
        cotacoesPorId={xCotacoesPorId}
        defaultIndicadorId={xDefaultBenchmark}
        headerHighlightEnabled={false}
        // indiceProps={{ subheaderEnabled: false }}
      />
    ),
    [ATIVO_OVERVIEW_SECTION.cotacao]: (
      <AtivoCotacao
        cotacao={cotacao}
        cotacoes={xAtivoCotacaoData}
        ativo={ativo}
        displayMode={xDisplayMode}
      />
    ),
    [ATIVO_OVERVIEW_SECTION.patrimonio]: <AtivoPatrimonio pessoaIds={[xPessoa.pessoa_id]} />,
    [ATIVO_OVERVIEW_SECTION.consistencia]: (
      <AtivoConsistencia ativos={[ativo]} cotacoesPorId={xCotacoesPorId} />
    ),
    [ATIVO_OVERVIEW_SECTION.metricasRisco]: <AtivoMetricasRisco ativos={[ativo]} />,
    [ATIVO_OVERVIEW_SECTION.graphs]: <Graphs isLoading={isAvaliacaoLoading} data={xCharts} />,
    [ATIVO_OVERVIEW_SECTION.imoveis]: <AtivoImoveis ativo={ativo} />
  };

  if (isPageLoading) {
    return (
      <Stack
        sx={{
          height: '100%'
        }}>
        <Stack justifyContent="center" alignItems="center" flexGrow={1}>
          <CircularProgress />
        </Stack>
      </Stack>
    );
  }

  if (validators.isEmpty(ativo)) {
    return (
      <Stack
        sx={{
          height: '100%'
        }}>
        <Stack justifyContent="center" alignItems="center" flexGrow={1}>
          <Typography variant="body2" color="textSecondary">
            Ativo não encontrado
          </Typography>
        </Stack>
      </Stack>
    );
  }
  return (
    <AtivoTemplate
      title={`${ativo.ticker || ativo.isin || ''} (${ativo.ativo || ''}) - ${
        xPessoa?.pessoa_fantasia || ''
      }`}
      header={<AtivoHeader compare={ativo.mercado === 'FND'} />}
      description={`Descubra tudo sobre ${ativo.ticker || ativo.isin} do ${
        xPessoa?.pessoa_fantasia || ''
      }: cotações, gráficos detalhados, resultados, dividendos, análises, balanços e indicadores financeiros. Acompanhe as últimas notícias e fatos relevantes para tomar decisões de investimento informadas.`}
      searchComponent={<SearchGlobal />} //SearchAtivos
      maxWidth="xl"
      ativo={ativo}>
      <Stack py={2}>
        <Divider />
      </Stack>

      <Stack gap={4}>
        <AtivoHighlight
          ativo={ativo}
          ativo_avaliacao={ativo_avaliacao}
          cotacao={cotacao}
          displayMode={xDisplayMode}
        />
        {xOverviewSections.gestorAdministrador && (
          <AtivoGestorAdministrador pessoa={xPessoa} ativo={ativo} displayMode={xDisplayMode} />
        )}
        {xOverviewSectionOrder
          .filter(pSection => xOverviewSections[pSection])
          .map(pSection => (
            <React.Fragment key={pSection}>{xContentBySection[pSection]}</React.Fragment>
          ))}
        {/* <AtivoIndicadores pessoa={xPessoa} ativo={ativo} cotacao={cotacao} />
        <AtivoProventos pessoa={xPessoa} ativo={ativo} /> */}
        {/* <AtivoCarteira ativo={ativo} /> */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              lg: 'repeat(2, 1fr)'
            },
            gap: 4
          }}>
          <AtivoInformacoes ativo={ativo} />
          <AtivoPessoa pessoa={xPessoa} />
        </Box>
        <AtivosSemelhantes ativo={ativo} />
      </Stack>
    </AtivoTemplate>
  );
});
export default AtivoOverview;
